import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

export type XtamDashClientRef = { key: string; label: string };

export type CityOperationalStats = {
  sitesTotal: number;
  sitesOn: number;
  sitesOff: number;
  camsTotal: number;
  camsOn: number;
  camsOff: number;
  sitesPct: number;
  camsPct: number;
};

function pct(on: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((on / total) * 1000) / 10;
}

function isOnState(state: unknown): boolean {
  if (state === 'on' || state === true || state === 1 || state === '1') return true;
  return false;
}

function cameraStatesFromSite(site: Record<string, unknown>): boolean[] {
  const out: boolean[] = [];
  const cams = site.cameras;
  if (!cams) return out;
  if (Array.isArray(cams)) {
    for (const item of cams) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      if ('state_cam' in row) {
        out.push(isOnState(row.state_cam));
        continue;
      }
      for (const val of Object.values(row)) {
        if (val && typeof val === 'object') {
          const inner = val as Record<string, unknown>;
          if ('state_cam' in inner) out.push(isOnState(inner.state_cam));
          else if ('state' in inner) out.push(isOnState(inner.state));
        }
      }
    }
    return out;
  }
  if (typeof cams === 'object') {
    for (const val of Object.values(cams as Record<string, unknown>)) {
      if (val && typeof val === 'object') {
        const inner = val as Record<string, unknown>;
        if ('state_cam' in inner) out.push(isOnState(inner.state_cam));
        else if ('state' in inner) out.push(isOnState(inner.state));
      }
    }
  }
  const n = Number(site.numcameras ?? site.numCameras ?? 0);
  if (out.length === 0 && n > 0 && 'state' in site) {
    const siteOn = isOnState(site.state);
    for (let i = 0; i < n; i++) out.push(siteOn);
  }
  return out;
}

function extractSiteRows(payload: unknown): Record<string, unknown>[] {
  if (!payload || typeof payload !== 'object') return [];
  const root = payload as Record<string, unknown>;
  const inner = (root.data ?? root) as Record<string, unknown>;
  let raw = inner.sites ?? root.sites;
  if (Array.isArray(raw)) return raw.filter((s) => s && typeof s === 'object') as Record<string, unknown>[];
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.sites)) {
      return obj.sites.filter((s) => s && typeof s === 'object') as Record<string, unknown>[];
    }
    return Object.values(obj).filter((s) => s && typeof s === 'object') as Record<string, unknown>[];
  }
  return [];
}

export function aggregateCityStats(payload: unknown): CityOperationalStats {
  const sites = extractSiteRows(payload);
  let sitesOn = 0;
  let sitesOff = 0;
  let camsOn = 0;
  let camsOff = 0;

  for (const site of sites) {
    const siteOn = isOnState(site.state);
    if (siteOn) sitesOn += 1;
    else sitesOff += 1;
    for (const camOn of cameraStatesFromSite(site)) {
      if (camOn) camsOn += 1;
      else camsOff += 1;
    }
  }

  const sitesTotal = sitesOn + sitesOff;
  const camsTotal = camsOn + camsOff;
  return {
    sitesTotal,
    sitesOn,
    sitesOff,
    camsTotal,
    camsOn,
    camsOff,
    sitesPct: pct(sitesOn, sitesTotal),
    camsPct: pct(camsOn, camsTotal),
  };
}

export async function fetchXtamDashboardClients(token: string): Promise<XtamDashClientRef[]> {
  const res = await axios.get(`${API_URL}/config/xtam-dashboard-clients`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.data ?? [];
}

export async function fetchCitySitesAndCams(
  token: string,
  clientKey: string,
  service = 'ALL'
): Promise<unknown> {
  const path =
    clientKey && clientKey.toUpperCase() !== 'LOCAL'
      ? `${API_URL}/xtam-dashboards/sitesandcams/${encodeURIComponent(clientKey)}`
      : `${API_URL}/xtam-dashboards/sitesandcams`;
  const res = await axios.get(path, {
    headers: { Authorization: `Bearer ${token}` },
    params: { service },
  });
  return res.data;
}
